import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, Briefcase, GraduationCap, Coffee } from "lucide-react";
import SmartImage from "@/components/SmartImage";

// Bundled locally in /public/thrive so images always load from the same origin.
const HERO_IMG = "/thrive/hero.jpg";
const COLLAGE = [
  "/thrive/col1.jpg",
  "/thrive/col2.jpg",
];

const groups = [
  {
    icon: Users,
    title: "Cohort circles",
    copy: "City-based groups on WhatsApp, Paris, Lyon, Bordeaux, Toulouse, Nice. Ask stupid questions, get real answers.",
    img: "/thrive/cohort.jpg",
  },
  {
    icon: Briefcase,
    title: "Internships & jobs",
    copy: "A curated board of internships (stages), student jobs, and post-graduation opportunities suitable for VLS-TS visa holders.",
    img: "/thrive/jobs.jpg",
  },
  {
    icon: Coffee,
    title: "IRL meetups",
    copy: "Café rendezvous, apartment-hunting mornings, museum walks, hosted monthly in every major French student city.",
    img: "/thrive/meetup.jpg",
  },
  {
    icon: GraduationCap,
    title: "Alumni network",
    copy: "Alumni now working in France or back in the US, the fastest way to get referrals when internship season starts.",
    img: "/thrive/alumni.jpg",
  },
];

export default function Thrive() {
  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-thrive">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">05 · Thrive</span>
        </div>

        <div className="grid md:grid-cols-12 gap-10 items-end">
          <h1 className="md:col-span-8 serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Arriving is <em className="italic text-[#b8892b]">the beginning</em>, not the end.
          </h1>
          <p className="md:col-span-4 text-base md:text-lg text-[#4a5570] leading-relaxed">
            Once you land, the story shifts from paperwork to belonging. FrancePath keeps you close, to your cohort,
            your city, and to the people who came before you.
          </p>
        </div>

        {/* Hero collage */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="mt-14 grid grid-cols-12 gap-3 md:gap-4" data-testid="thrive-hero-collage"
        >
          <div className="col-span-12 md:col-span-8 relative overflow-hidden aspect-[16/9] md:aspect-[16/10] bg-[#0d1b3e]">
            <SmartImage src={HERO_IMG} alt="Students in Paris" label="Paris · 6ᵉ" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b3e]/70 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
              <div>
                <div className="eyebrow !text-[#b8892b]">On the ground</div>
                <div className="serif text-2xl md:text-3xl leading-tight max-w-md mt-1">
                  Real friendships, formed slowly, held long.
                </div>
              </div>
              <div className="hidden md:block text-[10px] tracking-[0.22em] uppercase text-white/70">Paris · 6ᵉ</div>
            </div>
          </div>
          <div className="col-span-6 md:col-span-4 grid grid-rows-2 gap-3 md:gap-4">
            {COLLAGE.map((src, i) => (
              <div key={i} className="relative overflow-hidden bg-[#0d1b3e] aspect-[4/3] md:aspect-auto">
                <SmartImage src={src} alt="" label={i === 0 ? "Lyon" : "Marseille"} />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Group cards with imagery */}
        <div className="mt-20 grid md:grid-cols-2 gap-6" data-testid="thrive-groups">
          {groups.map((g, i) => (
            <motion.article
              key={g.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group bg-white border border-[#0d1b3e]/10 overflow-hidden"
              data-testid={`thrive-card-${g.title.toLowerCase().replace(/[^a-z]+/g,'-')}`}
            >
              <div className="grid grid-cols-5">
                <div className="col-span-2 relative overflow-hidden bg-[#0d1b3e]" style={{ minHeight: 220 }}>
                  <SmartImage
                    src={g.img}
                    alt={g.title}
                    label={g.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 w-10 h-10 bg-[#0d1b3e] text-[#b8892b] flex items-center justify-center z-10">
                    {React.createElement(g.icon, { size: 18 })}
                  </div>
                </div>
                <div className="col-span-3 p-6 md:p-8 flex flex-col justify-center">
                  <div className="serif text-2xl md:text-3xl text-[#0d1b3e] leading-tight">{g.title}</div>
                  <p className="text-[#4a5570] mt-3 leading-relaxed text-sm md:text-base">{g.copy}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-24 bg-[#0d1b3e] text-white p-10 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow text-[#b8892b] mb-2">Waitlist</div>
            <div className="serif text-4xl leading-tight">The community opens in cohorts, join to be added to the next intake.</div>
          </div>
          <div className="md:justify-self-end flex flex-wrap gap-3">
            <a href="https://wa.me/33618797689" target="_blank" rel="noreferrer" className="btn-sharp btn-sharp-gold" data-testid="thrive-whatsapp">
              WhatsApp us
            </a>
            <Link to="/signup" className="btn-sharp !bg-transparent !border-white/40 !text-white hover:!bg-white hover:!text-[#0d1b3e]" data-testid="thrive-signup">
              Create account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
